import classNames from 'classnames'
import { CSSProperties, ForwardedRef, ReactEventHandler, Ref, SyntheticEvent, forwardRef } from 'react'

import { useQuinceRouter as useRouter } from '@src/modules/navigation'
import { FEATURES, MODULES } from '@src/quince-site-common/src/constants/template-info'
import Link from '@src/quince-site-common/src/domains/Link'
import { HARD_CODED_IMAGE_QUALITY } from '@src/quince-site-common/src/utils/constants'
import { captureException } from '@src/quince-site-common/src/utils/sentryUtil'
import { clamp } from '@src/utils/functions/utils'

import styles from './image.module.scss'
import {
  IMAGE_DOMAIN_NAME,
  addImageRequestSource,
  getCommonImageVideoCSS,
  getErrorFromEvent,
  transformImageSrc,
} from './image.utils'

export interface ImageProps {
  id?: string
  url: string
  title: string
  image?: any
  className?: string
  description?: string
  fileName?: string
  height?: number
  width?: number
  backgroundColor?: string
  maxWidth?: number
  loading?: `lazy` | `eager`
  fadeIn?: boolean
  style?: CSSProperties
  useNative?: boolean
  fillWithBlackBackground?: boolean
  quality?: number
  link?: Link
  overrideMaxImageWidth1600?: boolean
  maxWidthMultiplier?: number
  hasDynamicImage?: boolean
  alt?: string
  skipSrcTransform?: boolean
  forceReduceQuality?: boolean
  fetchPriority?: 'high' | 'auto' | 'low'
  enforceFixedSize?: boolean
}

export const Image = forwardRef(
  (
    { loading = 'lazy', className = '', alt = '', url = '', id, title, ...props }: ImageProps,
    ref: ForwardedRef<HTMLImageElement>,
  ) => {
    const router = useRouter()

    const IMAGE_NOT_SUPPORTED_ERROR = 'Image is not supported anymore'
    if (props.image) {
      throw new Error(IMAGE_NOT_SUPPORTED_ERROR)
    }
    let imgSrc = props.skipSrcTransform ? url : transformImageSrc(url)
    if (props.forceReduceQuality) {
      imgSrc = imgSrc.includes('?')
        ? `${imgSrc}&q=${HARD_CODED_IMAGE_QUALITY}`
        : `${imgSrc}?q=${HARD_CODED_IMAGE_QUALITY}`
    }
    // Use Native only when explicitly asked, or the image url is not of contentful.
    if (
      // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
      props.useNative ||
      // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
      props.fillWithBlackBackground ||
      (!imgSrc.includes(IMAGE_DOMAIN_NAME) && !props.hasDynamicImage)
    ) {
      if (props.fillWithBlackBackground) {
        return (
          <div className={styles.background_black_wrapper}>
            <img
              id={id}
              title={title}
              data-testid={id}
              alt={alt || ''}
              // @i18n-ignore
              role={alt ? undefined : 'presentation'}
              ref={ref}
              loading={loading}
              className={classNames(className, styles.black_background_main_content)}
              src={addImageRequestSource(imgSrc)}
              onError={err => {
                captureException(getErrorFromEvent(err), {
                  module: MODULES.IMAGE_RENDER,
                  feature: FEATURES.IMAGE_FILL_WITH_BLACK_BACKGROUND,
                  extraInfo: {
                    imgSrc,
                  },
                })
              }}
            />
          </div>
        )
      } else {
        return (
          <img
            id={id}
            title={title}
            data-testid={id}
            alt={alt || ''}
            // @i18n-ignore
            role={alt ? undefined : 'presentation'}
            ref={ref}
            loading={loading}
            className={className}
            src={addImageRequestSource(imgSrc)}
            style={
              props.enforceFixedSize
                ? {
                    width: '100%',
                    height: '100%',
                    objectFit: 'contain',
                  }
                : undefined
            }
            onError={err => {
              captureException(getErrorFromEvent(err), {
                module: MODULES.IMAGE_RENDER,
                feature: FEATURES.IMAGE,
                extraInfo: {
                  imgSrc,
                },
              })
            }}
          />
        )
      }
    }

    const aspectRatio = props.width && props.height ? props.width / props.height : 1
    const { src, srcWebp, srcAvif, coverPaddingPercentage } = getGatsbyFluidImageProps(
      imgSrc,
      aspectRatio,
      props.maxWidth,
      props.quality,
      props.overrideMaxImageWidth1600,
      props.maxWidthMultiplier,
    )

    if (!src) return null

    const wrapperClassNames = classNames(
      className,
      props.link?.navigationPath && styles.wrapper__clickable,
      styles.wrapper,
    )

    const renderPicture = () => {
      return (
        <picture>
          {props.fetchPriority === 'high' && <source type="image/avif" srcSet={addImageRequestSource(srcAvif ?? '')} />}
          <source type="image/webp" srcSet={addImageRequestSource(srcWebp ?? '')} />
          <source srcSet={addImageRequestSource(src)} />
          <img
            id={id}
            title={title}
            data-testid={id}
            alt={alt || ''}
            // @i18n-ignore
            role={alt ? undefined : 'presentation'}
            loading={loading}
            src={addImageRequestSource(src)}
            style={{
              ...getCommonImageVideoCSS(),
            }}
            fetchPriority={props.fetchPriority ?? 'auto'}
            onError={err => {
              captureException(getErrorFromEvent(err), {
                module: MODULES.IMAGE_RENDER,
                feature: FEATURES.IMAGE_WITH_SRC,
                extraInfo: {
                  src,
                  srcWebp,
                },
              })
            }}
          ></img>
        </picture>
      )
    }

    const handleClick = (navigationPath: string): ReactEventHandler => {
      if (!navigationPath) return () => null
      return (event: SyntheticEvent) => {
        if (navigationPath) {
          event.isHandled = true
          router.push(navigationPath)
        }
      }
    }

    return (
      <div ref={ref} className={wrapperClassNames} onClick={handleClick(props.link?.navigationPath ?? '')}>
        <div
          className={styles.imageBody}
          style={{
            backgroundColor: props.backgroundColor,
            ...props.style,
          }}
        >
          {/* @i18n-ignore */}
          <div aria-hidden="true" style={{ width: '100%', paddingBottom: `${coverPaddingPercentage}%` }}></div>
          {renderPicture()}
        </div>
      </div>
    )
  },
)

// @i18n-ignore
Image.displayName = 'Image'
const WEBP_FORMAT = 'webp'
const AVIF_FORMAT = 'avif'

const getImageURLparams = ({ width, height, quality }: { width: number; height: number; quality: number }) => {
  const MAX_HEIGHT = 3000
  const shouldPushHeight = height <= MAX_HEIGHT
  const params: any = {
    w: width,
    q: quality,
  }
  if (shouldPushHeight) {
    params.h = height
  }

  const webp = new URLSearchParams({
    ...params,
    // @i18n-ignore
    fm: WEBP_FORMAT,
  }).toString()

  const avif = new URLSearchParams({
    ...params,
    // @i18n-ignore
    fm: AVIF_FORMAT,
  }).toString()

  const fallback = new URLSearchParams({
    ...params,
  }).toString()

  return {
    fallback,
    webp,
    avif,
  }
}
const getGatsbyFluidImageProps = (
  url: string,
  aspectRatio: number,
  maxWidth = 1600,
  quality = 50,
  overrideMax1600 = false,
  maxWidthMultiplier = 2,
) => {
  const _quality = quality > 100 ? 100 : quality
  const reverseAspectRatio = 1 / aspectRatio
  let customWidth =
    overrideMax1600 && maxWidth
      ? Math.round(maxWidth * maxWidthMultiplier)
      : clamp(Math.round(maxWidth * maxWidthMultiplier), 1600)
  customWidth = Number.isFinite(customWidth) && customWidth > 0 ? customWidth : 1600
  const customHeight = Math.ceil(customWidth * reverseAspectRatio)

  const { fallback, webp, avif } = getImageURLparams({
    width: customWidth,
    height: customHeight,
    quality: _quality,
  })

  const isSVG = /\.(svg)$/.test(url)

  return {
    coverPaddingPercentage: (customHeight / customWidth) * 100,
    src: `${url}?${fallback}`,
    srcWebp: !isSVG ? `${url}?${webp}` : undefined,
    srcAvif: !isSVG ? `${url}?${avif}` : undefined,
  }
}

export type ImageRefType = Ref<HTMLDivElement>

export default Image
